/**
 * VELUNE — CART RESERVATION TIMER V3
 *
 * Purpose:
 * - Make the cart timer available before the cart receives its first item.
 * - Synchronize with the Velune / Kaching Final Collection countdown.
 * - Survive Shopify cart section morphs.
 * - Avoid MutationObserver and polling.
 * - Remain safe when sessionStorage is unavailable.
 * - Stop its interval automatically when no countdown nodes remain.
 */

const VELUNE_TIMER_STATE_KEY = '__veluneFinalCollectionUrgency';
const VELUNE_TIMER_STORAGE_KEY = 'veluneFinalCollectionUrgencyEnd';

class VeluneCartReservationTimer extends HTMLElement {
  connectedCallback() {
    this.registerTimer();
  }

  registerTimer() {
    if (!this.isConnected) return;

    const existingState = window[VELUNE_TIMER_STATE_KEY];

    if (existingState && typeof existingState.register === 'function') {
      existingState.register(this);

      if (typeof existingState.tick === 'function') {
        existingState.tick();
      }

      return;
    }

    const inheritedEnd = Number(existingState?.end);
    const rawMinutes = Number(this.dataset.minutes || '47');
    const fallbackMinutes = Number.isFinite(rawMinutes)
      ? Math.max(1, Math.min(59, rawMinutes))
      : 47;
    const now = Date.now();

    let storedEnd = Number.NaN;

    try {
      storedEnd = Number(sessionStorage.getItem(VELUNE_TIMER_STORAGE_KEY));
    } catch (_) {
      // sessionStorage can be unavailable in privacy-restricted contexts.
    }

    let end;

    if (Number.isFinite(inheritedEnd) && inheritedEnd > now) {
      end = inheritedEnd;
    } else if (Number.isFinite(storedEnd) && storedEnd > now) {
      end = storedEnd;
    } else {
      end = now + fallbackMinutes * 60 * 1000;
      storedEnd = end;
    }

    try {
      sessionStorage.setItem(VELUNE_TIMER_STORAGE_KEY, String(end));
    } catch (_) {
      // sessionStorage unavailable: timer still works in memory.
    }

    const state = {
      end,
      interval: null,
      nodes: new Set(),
      register: null,
      tick: null,
    };

    const formatTime = () => {
      const remainingMilliseconds = Math.max(0, state.end - Date.now());
      const totalSeconds = Math.floor(remainingMilliseconds / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      return [hours, minutes, seconds]
        .map((value) => String(value).padStart(2, '0'))
        .join(':');
    };

    const stopInterval = () => {
      if (!state.interval) return;
      window.clearInterval(state.interval);
      state.interval = null;
    };

    const tick = () => {
      const value = formatTime();

      for (const node of state.nodes) {
        if (!node?.isConnected) {
          state.nodes.delete(node);
          continue;
        }

        if (node.textContent !== value) {
          node.textContent = value;
        }
      }

      if (state.nodes.size === 0 || value === '00:00:00') {
        stopInterval();
      }
    };

    const register = (root) => {
      if (!root) return;

      if (root.matches?.('[data-vkb-countdown]')) {
        state.nodes.add(root);
      }

      root
        .querySelectorAll?.('[data-vkb-countdown]')
        .forEach((node) => state.nodes.add(node));

      tick();

      if (!state.interval && state.nodes.size > 0 && formatTime() !== '00:00:00') {
        state.interval = window.setInterval(tick, 1000);
      }
    };

    state.register = register;
    state.tick = tick;

    window[VELUNE_TIMER_STATE_KEY] = state;

    register(this);
  }
}

if (!customElements.get('velune-cart-reservation-timer')) {
  customElements.define('velune-cart-reservation-timer', VeluneCartReservationTimer);
}
